import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, MessageCircle, Mail, Send, CheckCircle2, Loader2 } from 'lucide-react';
import BrandMark from '@/components/BrandMark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success('Thanks — we\'ll be in touch soon!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send your message — please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="contact-page">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center"><BrandMark size={36} /></Link>
          <Link to="/" data-testid="contact-back">
            <Button variant="ghost" className="text-slate-600 hover:text-fern hover:bg-fern/5 rounded-lg gap-2">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Back</span>
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-fern/10 mb-5">
            <MessageCircle className="w-6 h-6 text-fern" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-fern font-semibold mb-3">Get in touch</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-fern-dark tracking-tight">
            Kia ora — how can we help?
          </h1>
          <p className="text-slate-600 text-base sm:text-lg mt-4 max-w-xl mx-auto leading-relaxed">
            Questions about a lesson, your purchase, or how NestWise works for your whānau?
            Drop us a note — we read every message.
          </p>
        </div>

        {sent ? (
          <Card data-testid="contact-success" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
            <CardContent className="p-8 sm:p-10 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-fern text-white mb-4 shadow-lg shadow-fern/30">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-fern-dark mb-2">Message sent</h2>
              <p className="text-slate-600 text-sm leading-relaxed mb-5">
                Ngā mihi! We've received your note and will reply to <strong>{form.email}</strong> as soon as we can — usually within a couple of working days.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  data-testid="contact-send-another"
                  variant="outline"
                  onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                  className="rounded-full border-fern/30 text-fern hover:bg-fern/5"
                >
                  Send another message
                </Button>
                <Link to="/"><Button className="bg-fern hover:bg-fern-light text-white rounded-full">Back to home</Button></Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.06)] rounded-3xl overflow-hidden">
            <CardContent className="p-6 sm:p-10">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact-name">Your name</Label>
                    <Input
                      id="contact-name"
                      data-testid="contact-name"
                      placeholder="e.g. Aroha"
                      value={form.name}
                      onChange={e => update('name', e.target.value)}
                      required
                      className="rounded-xl mt-1.5 bg-slate-50 focus:bg-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-email">Email</Label>
                    <Input
                      id="contact-email"
                      data-testid="contact-email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={e => update('email', e.target.value)}
                      required
                      className="rounded-xl mt-1.5 bg-slate-50 focus:bg-white"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-subject">Subject</Label>
                  <Input
                    id="contact-subject"
                    data-testid="contact-subject"
                    placeholder="What's this about?"
                    value={form.subject}
                    onChange={e => update('subject', e.target.value)}
                    required
                    maxLength={160}
                    className="rounded-xl mt-1.5 bg-slate-50 focus:bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-message">Message</Label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message"
                    placeholder="Tell us a bit more…"
                    value={form.message}
                    onChange={e => update('message', e.target.value)}
                    required
                    rows={6}
                    maxLength={5000}
                    className="rounded-xl mt-1.5 bg-slate-50 focus:bg-white resize-none"
                  />
                  <p className="text-xs text-slate-400 mt-1.5 text-right">{form.message.length} / 5000</p>
                </div>
                <Button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="w-full bg-fern hover:bg-fern-light text-white rounded-full py-3 font-medium gap-2 transition-all hover:scale-[1.01] active:scale-95"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send message <Send className="w-4 h-4" /></>}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="mt-10 rounded-3xl border border-fern/15 bg-fern/5 px-6 py-5 flex items-start gap-3">
          <Mail className="w-5 h-5 text-fern flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">
            Prefer email? You can also reach us directly at{' '}
            <a href="mailto:apcollective4@gmail.com" data-testid="contact-direct-email" className="text-fern font-semibold hover:underline">
              apcollective4@gmail.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
